// src/hooks/useNotifications.js
//
// Aggregates the notification bell's contents from the signals the platform
// already produces.
//
// DEF-027 — "Notification bell non-functional across Patient, Navigator,
// Doctor." The bell was literally this, in Layout.js:
//
//     <button className="topbar-icon-btn" title="Notifications">🔔</button>
//
// No handler, no data, no panel. It rendered in all three portals and did
// nothing in any of them. TC-N002 was blocked by the same gap from the other
// side: the navigator's 2-hour escalation SLA badge had no surface to appear on,
// because "the escalation surface does not exist in the UI".
//
// WHY AGGREGATE RATHER THAN ADD A NOTIFICATIONS TABLE
// ---------------------------------------------------
// Every signal a user would want in a bell is already an endpoint:
//
//   unread messages   → GET /conversations       (unreadCount per thread)
//   open care actions → GET /patients/{id}/care-plan
//   escalations       → GET /doctor/critical-escalations
//
// A notifications table would be a second copy of that state, needing a writer
// on every path that can raise one and a reconciliation story when the two
// disagree. Deriving the list at read time cannot drift from the thing it
// describes. If notifications later need to be individually dismissible or to
// outlive their source, that is the point to introduce storage — noting it here
// so the trade-off is a decision rather than an omission.
//
// The cost is one fan-out per poll. Promise.allSettled means a single failing
// endpoint degrades that section only, rather than emptying the bell.

import { useState, useEffect, useCallback, useRef } from 'react';
import { doctorTab } from '../utils/escalationState';
import { labOrdersFrom, labTestReminders } from '../utils/labTestReminders';
import { labReportAlerts } from '../utils/labReportAlerts';
import { isLabTestSuggestion } from '../utils/labTestSuggestion';
import {
  NOTIFICATIONS_READ_EVENT, isRead, loadReadState, readStateKey, saveReadState, withAllMarkedRead, withMarkedRead,
} from '../utils/notificationReadState';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

// Polling interval. Deliberately slow: the bell is an ambient indicator, not a
// live feed — the chat surfaces have their own SSE stream for anything that
// needs to arrive immediately (see useHumanChat). A 60s bell costs three
// requests a minute per open tab and nothing in perceived latency, because the
// user is not watching it.
const POLL_INTERVAL_MS = 60_000;

/** Sort order — most urgent first, then newest. */
const SEVERITY_RANK = { CRITICAL: 0, URGENT: 0, HIGH: 1, INFO: 2 };

/** FollowUpEscalationDTO.Response.level -> bell copy. Day 2/3 read as URGENT — by then it's genuinely overdue. */
const FOLLOW_UP_LEVEL_META = {
  FIRST_NOTICE: { severity: 'HIGH',   title: 'Follow-up not yet booked' },
  DAY_1:        { severity: 'HIGH',   title: 'Follow-up still not booked — 1 day' },
  DAY_2:        { severity: 'URGENT', title: 'Follow-up still not booked — 2 days' },
  DAY_3:        { severity: 'URGENT', title: 'Follow-up still not booked — 3 days' },
};

function authHeaders() {
  const jwt = localStorage.getItem('previu_token');
  return {
    'Content-Type': 'application/json',
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  };
}

async function getJson(path) {
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  if (res.status === 204) return null;
  return res.json();
}

/**
 * @param {object}  options
 * @param {'PATIENT'|'NAVIGATOR'|'DOCTOR'} options.role  which portal is asking —
 *        decides which sources are polled and where each item links to
 * @param {string}  options.patientId  required for role PATIENT (care plan)
 * @param {boolean} options.enabled    false while the session is still loading
 * @param {boolean} [options.canReview] true only for a DOCTOR or HEALTH_NAVIGATOR account, the
 *        two roles the AI report review queue exists for. An ADMIN is shown the NAVIGATOR bell but
 *        the endpoint answers it 403, and a 403 from a fetch() to the API is treated as an expired
 *        session (utils/sessionExpiry.js), which signed admins out on every login.
 * @param {boolean} [options.canFollowUp] true only for a HEALTH_NAVIGATOR account. The follow-up
 *        endpoint is `hasRole('HEALTH_NAVIGATOR')`, so an ADMIN (shown the NAVIGATOR bell) gets a
 *        403, which a fetch() guard treats as an expired session and signs the admin out.
 * @param {boolean} [options.canEscalate] true only for a DOCTOR, HEALTH_NAVIGATOR or ADMIN account.
 *        Both escalation sources are under /doctor/**, which SecurityConfig limits to those three,
 *        so a SUPPORT_STAFF account (shown the NAVIGATOR bell) got a 403 and was signed out.
 * @param {object}  [options.plan]     patient plan from PatientContext. When
 *        the patient is neither on a paid plan nor covered by a subscriber, a
 *        standing "upgrade" item is added and stays until that changes — it is
 *        derived state, not a polled source, so it needs no request.
 * @param {boolean} [options.appNotificationsOff] the patient turned in-app
 *        notifications off (profile.appNotificationPreference === false). Only
 *        honoured for role PATIENT — staff bells are never affected. When set,
 *        nothing is fetched or polled and the item list is empty.
 */
export function useNotifications({ role, patientId, enabled = true, plan, canReview = false, canFollowUp = false, canEscalate = false, appNotificationsOff = false }) {
  // Everything the sources currently report, read or not. `items` returned
  // below is this minus what the user has already cleared.
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Read state is applied on top of each poll — see utils/notificationReadState.js.
  const storageKey = readStateKey(role, patientId);
  const [readState, setReadState] = useState(() => loadReadState(storageKey));
  const readStateRef = useRef(readState);
  const allItemsRef = useRef([]);
  useEffect(() => {
    const loaded = loadReadState(storageKey);
    readStateRef.current = loaded;
    setReadState(loaded);
  }, [storageKey]);

  // Survives re-renders so an in-flight poll that resolves after unmount (or
  // after the role changes) cannot write into stale state.
  const generationRef = useRef(0);

  const turnedOff = role === 'PATIENT' && appNotificationsOff;
  const active = enabled && !turnedOff;

  const load = useCallback(async () => {
    if (!active) return;
    const generation = ++generationRef.current;

    const sources = [getJson('/conversations')];

    // Care plan is patient-scoped. A navigator's or doctor's queue is
    // per-patient too and would need a patient in view, so their equivalent is
    // the escalation list below rather than a care plan.
    sources.push(role === 'PATIENT' && patientId
      ? getJson(`/patients/${patientId}/care-plan`)
      : Promise.resolve(null));

    // Escalations are staff-only. Requesting them as a patient would 403 —
    // handled by allSettled, but not asking is clearer than swallowing a
    // predictable rejection.
    //
    // No patientId: the endpoint returns the OPEN queue across all patients.
    // It used to 400 without one, which is why there was no escalation surface
    // at all (TC-N002) — see DoctorPortalController.getCriticalEscalations.
    sources.push(role === 'PATIENT' || !canEscalate
      ? Promise.resolve(null)
      : getJson(role === 'DOCTOR'
          // A doctor's list includes what a navigator already acknowledged but
          // no doctor has reviewed, which the default (open) view leaves out.
          ? '/doctor/critical-escalations?view=ACTIVE'
          : '/doctor/critical-escalations'));

    // Navigator-only — "book this follow-up" reminders. Self-scoped to the
    // calling navigator's own assigned patients (unlike the org-wide
    // critical-escalations queue above), so a DOCTOR would 403 too — see
    // FollowUpEscalationController's javadoc for why.
    sources.push(role === 'NAVIGATOR' && canFollowUp
      ? getJson('/navigator/follow-up-escalations')
      : Promise.resolve(null));

    // Navigator-only — escalations a doctor has handed to this navigator. They
    // are already acknowledged, so the open queue above no longer lists them;
    // without this the hand-off would be silent.
    sources.push(role === 'NAVIGATOR' && canEscalate
      ? getJson('/doctor/critical-escalations?view=ACTIVE&assignedToMe=true')
      : Promise.resolve(null));

    // Patient-only — lab orders, for the "test tomorrow / today" reminders.
    sources.push(role === 'PATIENT' && patientId
      ? getJson(`/lab-test-orders?patientId=${patientId}&page=0&size=20`)
      : Promise.resolve(null));

    // Patient-only — health records, for the "your lab report is ready" alert.
    sources.push(role === 'PATIENT' && patientId
      ? getJson(`/patients/${patientId}/health-records`)
      : Promise.resolve(null));

    // Staff-only — AI-analysed reports waiting for this reviewer's sign-off.
    sources.push(role !== 'PATIENT' && canReview
      ? getJson('/report-reviews/pending')
      : Promise.resolve(null));

    const [conversationsRes, carePlanRes, escalationsRes, followUpRes, assignedRes, labOrdersRes, recordsRes, reviewsRes] =
      await Promise.allSettled(sources);

    if (generation !== generationRef.current) return;

    const next = [];
    let anySourceFailed = false;

    // ── Unread messages ───────────────────────────────────────────────────
    if (conversationsRes.status === 'fulfilled' && Array.isArray(conversationsRes.value)) {
      conversationsRes.value
        .filter(c => (c.unreadCount || 0) > 0)
        .forEach(c => next.push({
          id: `msg-${c.id}`,
          severity: 'INFO',
          icon: '💬',
          title: `${c.unreadCount} new message${c.unreadCount === 1 ? '' : 's'}`,
          body: c.counterpartName
            ? `From ${c.counterpartName}`
            : 'In one of your conversations',
          at: c.lastMessageAt || c.createdAt,
          // Deep-links to the specific thread (see utils/notificationTarget.js).
          // Without conversationId, every message notification for the same
          // role pointed at the same generic inbox URL, so clicking one just
          // opened whichever conversation that inbox's own "auto-select most
          // recently active" effect had already picked — not the one the
          // notification was actually about.
          kind: 'chat',
          ref: { conversationId: c.id, conversationType: c.conversationType },
        }));
    } else if (conversationsRes.status === 'rejected') {
      anySourceFailed = true;
    }

    // ── Open urgent care plan items ───────────────────────────────────────
    // Only URGENT/HIGH reach the bell. The care plan page is the place to read
    // the whole plan; a bell that lists every routine monitoring item is the
    // alert fatigue the Constitution names as a patient harm (§I.1 Principle 05).
    if (carePlanRes.status === 'fulfilled' && Array.isArray(carePlanRes.value)) {
      carePlanRes.value
        .filter(i => !i.completed && (i.priority === 'URGENT' || i.priority === 'HIGH'))
        .forEach(i => next.push({
          id: `care-${i.id}`,
          severity: i.priority,
          icon: i.priority === 'URGENT' ? '🚨' : '⚠️',
          title: i.priority === 'URGENT' ? 'Urgent care plan action' : 'Care plan action',
          // description already arrives as clean clinical text — the server
          // strips its own [AUTO] metadata now (see ActionItemDescription).
          body: i.description,
          at: i.createdAt,
          kind: 'carePlan',
          // A doctor's lab-test suggestion goes straight to the order flow.
          ref: { patientId, itemId: i.id, labTestSuggestion: isLabTestSuggestion(i.description) },
        }));
    } else if (carePlanRes.status === 'rejected') {
      anySourceFailed = true;
    }

    // ── Lab-day reminders (patient) ───────────────────────────────────────
    // Same moments as the backend's WhatsApp reminders (see
    // utils/labTestReminders.js). HIGH, like an open care-plan action: worth
    // acting on today, not a clinical emergency.
    if (labOrdersRes.status === 'fulfilled' && labOrdersRes.value) {
      labTestReminders(labOrdersFrom(labOrdersRes.value)).forEach(r => next.push({
        id: `lab-${r.id}`,
        severity: 'HIGH',
        icon: '🧪',
        title: r.title,
        body: r.meta,
        at: new Date().toISOString(),
        // Stamped "now" on every poll, so reading one would un-read it on the
        // next. The reminder is per day: cleared today, back tomorrow.
        revisedAt: startOfToday(),
        kind: 'labDay',
        ref: { patientId, orderId: r.id },
      }));
    } else if (labOrdersRes.status === 'rejected') {
      anySourceFailed = true;
    }

    // ── Lab report arrived (patient) ──────────────────────────────────────
    // A finished report imported from the lab partner (see utils/labReportAlerts.js).
    if (recordsRes.status === 'fulfilled' && recordsRes.value) {
      labReportAlerts(Array.isArray(recordsRes.value) ? recordsRes.value : recordsRes.value.content)
        .forEach(a => next.push({
          id: `report-${a.id}`,
          severity: 'HIGH',
          icon: '📄',
          title: a.title,
          body: a.body,
          at: a.at,
          kind: 'labReport',
          ref: { patientId, recordId: a.id, triageResultId: a.triageResultId },
        }));
    } else if (recordsRes.status === 'rejected') {
      anySourceFailed = true;
    }

    // ── AI reports awaiting review (staff) ────────────────────────────────
    // Doctor and navigator each have their own queue; the server drops a
    // report from yours once you have reviewed it.
    if (reviewsRes.status === 'fulfilled' && Array.isArray(reviewsRes.value)) {
      reviewsRes.value.forEach(r => next.push({
        id: `review-${r.triageResultId}`,
        severity: r.hasAcuteAlert ? 'HIGH' : 'INFO',
        icon: '🧠',
        title: 'AI report awaiting your review',
        body: [r.patientName, r.acuteAlertDetail || r.keyFindings].filter(Boolean).join(' — '),
        at: r.createdAt,
        kind: 'review',
        ref: { patientId: r.patientId, triageResultId: r.triageResultId },
      }));
    } else if (reviewsRes.status === 'rejected') {
      anySourceFailed = true;
    }

    // ── Critical escalations (staff) ──────────────────────────────────────
    // TC-N002: this is the surface the 2-hour SLA badge was missing. slaOverdue
    // is computed here from notifiedAt rather than trusting a server field that
    // may not exist yet, so the badge is correct even before the API grows one.
    if (escalationsRes.status === 'fulfilled' && Array.isArray(escalationsRes.value)) {
      escalationsRes.value
        .filter(e => role !== 'DOCTOR' || doctorTab(e) === 'open')
        .forEach(e => {
        const overdue = isSlaBreached(e);
        next.push({
          id: `esc-${e.id}`,
          severity: 'CRITICAL',
          icon: '🚨',
          title: overdue
            ? 'Escalation OVERDUE — 2h SLA breached'
            : e.status === 'ACKNOWLEDGED'
              ? 'Critical escalation awaiting your review'
              : 'Critical escalation awaiting acknowledgement',
          body: e.acuteAlertDetail || `Risk level ${e.riskLevel || 'HIGH'}`,
          at: e.notifiedAt || e.createdAt,
          // Deep-links to the specific patient this escalation is about —
          // same fix as message notifications above. This always pointed at
          // the plain patient LIST page regardless of which escalation was
          // clicked, so a doctor with several open escalations had to find
          // the right patient themselves after landing on My Patients; the
          // escalation response already carries patientId, just unused here.
          kind: 'escalation',
          ref: { patientId: e.patientId, escalationId: e.id },
        });
      });
    } else if (escalationsRes.status === 'rejected') {
      anySourceFailed = true;
    }

    // ── Escalations assigned to this navigator ────────────────────────────
    if (assignedRes.status === 'fulfilled' && Array.isArray(assignedRes.value)) {
      assignedRes.value
        .filter(e => e.status === 'ACKNOWLEDGED')
        .forEach(e => {
          const needsBooking = e.suggestedSpecialty && !e.specialistBookedAt;
          next.push({
            id: `esc-assigned-${e.id}`,
            severity: e.priority === 'CRITICAL' ? 'CRITICAL' : 'HIGH',
            icon: needsBooking ? '🩺' : '👤',
            title: needsBooking
              ? 'Specialist appointment to book'
              : 'Escalation assigned to you',
            body: [e.patientName, e.acuteAlertDetail].filter(Boolean).join(' — '),
            at: e.assignedAt || e.createdAt,
            kind: 'escalationAssigned',
            ref: { patientId: e.patientId, escalationId: e.id },
          });
        });
    } else if (assignedRes.status === 'rejected') {
      anySourceFailed = true;
    }

    // ── Follow-up escalations (navigator) ─────────────────────────────────
    // "Navigator should get an alert... till he books an appointment with
    // the patient" — one item for the current 1/2/3-day cadence level, plus
    // a SEPARATE item once the proposed date is within 5 days (an addition
    // to the cadence, not a replacement for it).
    if (followUpRes.status === 'fulfilled' && Array.isArray(followUpRes.value)) {
      followUpRes.value.forEach(f => {
        const meta = FOLLOW_UP_LEVEL_META[f.level] || FOLLOW_UP_LEVEL_META.FIRST_NOTICE;
        next.push({
          id: `followup-${f.id}`,
          severity: meta.severity,
          icon: '📅',
          title: meta.title,
          body: f.patientName ? `With ${f.patientName}` : 'Book to confirm',
          at: f.firstNotifiedAt,
          kind: 'followUp',
          ref: { patientId: f.patientId, escalationId: f.id },
        });
        if (f.fiveDayBeforeActive) {
          next.push({
            id: `followup-${f.id}-5day`,
            severity: 'HIGH',
            icon: '⏰',
            title: 'Follow-up date is approaching',
            body: f.patientName
              ? `${f.patientName}'s proposed follow-up is in 5 days or less — still not booked`
              : 'Proposed follow-up in 5 days or less — still not booked',
            at: f.firstNotifiedAt,
            kind: 'followUp',
            ref: { patientId: f.patientId, escalationId: f.id },
          });
        }
      });
    } else if (followUpRes.status === 'rejected') {
      anySourceFailed = true;
    }

    // ── Standing upgrade prompt (patient, free tier) ──────────────────────
    // Not polled — derived from the plan the context already resolved, so it
    // costs no request and persists across reloads until the patient is
    // actually on a paid plan. Deliberately INFO: it sorts below every
    // clinical item, so a commercial nudge can never outrank an urgent care
    // plan action in the bell (alert fatigue — Constitution §I.1 Principle 05).
    // Suppressed for a patient covered by a subscriber, who must not be sold
    // a second plan.
    if (role === 'PATIENT' && plan && !plan.loading && !plan.isPaid) {
      next.push({
        id: 'plan-upgrade',
        severity: 'INFO',
        icon: '⭐',
        title: "You're on the Free plan",
        body: 'Upgrade to unlock the full Previu programme.',
        // No `at`: this is a standing state, not an event, and stamping it
        // with "now" on every poll would make it look perpetually new.
        at: null,
        kind: 'plan',
        ref: { patientId },
      });
    }

    next.sort((a, b) => {
      const bySeverity = (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9);
      if (bySeverity !== 0) return bySeverity;
      return new Date(b.at || 0) - new Date(a.at || 0);
    });

    allItemsRef.current = next;
    setAllItems(next);
    // Only surface an error when EVERY source failed — one endpoint being down
    // shouldn't make the bell claim it is broken while still holding real items.
    setError(anySourceFailed && next.length === 0
      ? 'Could not load notifications.'
      : null);
    setLoading(false);
  }, [role, patientId, active, canReview, canFollowUp, canEscalate, plan?.isPaid, plan?.loading]);

  // Turning in-app notifications off clears what is already shown, not just
  // future polls. The generation bump discards a poll that was in flight when
  // the preference flipped. Turning it back on re-runs the effect below, which
  // loads straight away — no reload needed either way.
  useEffect(() => {
    if (!turnedOff) return;
    generationRef.current++;
    allItemsRef.current = [];
    setAllItems([]);
    setError(null);
    setLoading(false);
  }, [turnedOff]);

  useEffect(() => {
    if (!active) return undefined;
    load();
    const timer = setInterval(load, POLL_INTERVAL_MS);

    // Refresh on tab focus as well as on the timer: coming back to a tab that
    // has been open for an hour should not show an hour-old bell for up to a
    // minute. Same reasoning as the visibility handling in useHumanChat.
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      // Invalidate any in-flight poll so it cannot setState after unmount.
      generationRef.current++;
    };
  }, [load, active]);

  // ── Read / unread ─────────────────────────────────────────────────────
  const commitReadState = useCallback((nextState) => {
    readStateRef.current = nextState;
    setReadState(nextState);
    saveReadState(storageKey, nextState);
  }, [storageKey]);

  const markRead = useCallback((targets) => {
    const wanted = new Set(targets.map(t => (typeof t === 'string' ? t : t.id)));
    const current = readStateRef.current;
    const fresh = allItemsRef.current.filter(i => wanted.has(i.id) && !isRead(i, current));
    if (fresh.length === 0) return;
    commitReadState(withMarkedRead(current, fresh));
  }, [commitReadState]);

  // A watermark rather than a marker per item — see allReadUpTo in
  // utils/notificationReadState.js.
  const markAllRead = useCallback(() => {
    const current = readStateRef.current;
    const fresh = allItemsRef.current.filter(i => !isRead(i, current));
    if (fresh.length === 0) return;
    commitReadState(withAllMarkedRead(current, fresh));
  }, [commitReadState]);

  // Screens that clear a notification as a side effect (opening a care-team
  // conversation) ask by id rather than owning this state.
  useEffect(() => {
    const onRequest = (e) => markRead(e.detail?.ids || []);
    window.addEventListener(NOTIFICATIONS_READ_EVENT, onRequest);
    return () => window.removeEventListener(NOTIFICATIONS_READ_EVENT, onRequest);
  }, [markRead]);

  const items = allItems.filter(i => !isRead(i, readState));

  return {
    turnedOff,
    items,
    readItems: readState.history,
    loading,
    error,
    unreadCount: items.length,
    hasCritical: items.some(i => i.severity === 'CRITICAL' || i.severity === 'URGENT'),
    markRead,
    markAllRead,
    refresh: load,
  };
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * The Constitution §III requires a CRITICAL escalation to be acknowledged within
 * 2 hours. An escalation notified longer ago than that and still unacknowledged
 * is in breach.
 *
 * Mirrors CriticalEscalationService.SLA_HOURS on the server. Duplicated
 * deliberately: this is a display decision about a value the API already
 * returns, and the alternative is showing nothing until the API grows an
 * `slaOverdue` field. If the SLA changes, both need updating — flagged so that
 * is a known cost rather than a surprise.
 */
const SLA_HOURS = 2;

function isSlaBreached(escalation) {
  if (escalation.status === 'ACKNOWLEDGED') return false;
  if (escalation.status === 'ESCALATED') return true;
  const notifiedAt = escalation.notifiedAt;
  if (!notifiedAt) return false;
  const hoursSince = (Date.now() - new Date(notifiedAt).getTime()) / 3_600_000;
  return hoursSince >= SLA_HOURS;
}
